import { useEffect, useRef, useState } from 'react'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { PRODUCT_ENTITLEMENTS, type ReportSettings } from '@headline/schemas'
import { PageHeader } from '@/components/PageHeader'
import { DeleteReportButton } from '@/components/reports/DeleteReportButton'
import { ExportReportConfigButton } from '@/components/reports/ExportReportConfigButton'
import { PreviewTodayButton } from '@/components/reports/PreviewTodayButton'
import { ReportStatusToggle } from '@/components/reports/ReportStatusToggle'
import { useCanEditAccount } from '@/lib/use-account-permissions'
import { ContentTab } from '@/components/reports/ContentTab'
import { DeliveryTab } from '@/components/reports/DeliveryTab'
import { DerekTab } from '@/components/reports/DerekTab'
import { HeadersTab } from '@/components/reports/HeadersTab'
import { TemplateTab } from '@/components/reports/TemplateTab'
import { withField } from '@/components/reports/coverage-fields'
import { buttonVariants } from '@/components/ui/button'
import {
  coverageListsQuery,
  reportQuery,
  accountQuery,
  selectableTemplatesQuery,
  useUpdateReport,
} from '@/lib/queries'

// Delivery first, and the tab a report opens on (Phoebe's Report Config feedback, Oct 2026):
// when it goes out and to whom is what an associate checks most. This reverses the 2026-08-25
// tech sync's Content-first order.
const TABS = ['delivery', 'content', 'headers', 'template', 'derek'] as const

type Tab = (typeof TABS)[number]

const TAB_LABEL: Record<Tab, string> = {
  delivery: 'Delivery',
  content: 'Content',
  headers: 'Headers',
  template: 'Template',
  derek: 'Derek prompts',
}

/**
 * Report configuration — click-through tabs, each a validated search param. Owns WHAT DATA
 * EXISTS and when it goes out; presentation lives in the template, brand on the account.
 * Everything AUTO-SAVES: settings debounce ~600ms, structural edits (headers, coverage,
 * lists) save immediately. No Save button anywhere, per Kevin.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/reports/$reportId')({
  // `.default` as well as `.catch`, so `tab` is optional at every link: which tab opens first is
  // decided here and nowhere else. Two links used to pin `tab: 'delivery'`, which is why changing
  // the fallback alone did nothing.
  validateSearch: z.object({ tab: z.enum(TABS).default('delivery').catch('delivery') }),
  loader: async ({ context, params }) => {
    // Reports is a product — an account without it has no report surface, so guard the detail page
    // too (a deep link, not just the list). The account also carries the brand a report follows.
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.reports)) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    await Promise.all([
      context.queryClient.ensureQueryData(reportQuery(params.reportId)),
      context.queryClient.ensureQueryData(coverageListsQuery(params.accountId)),
      context.queryClient.ensureQueryData(selectableTemplatesQuery(params.accountId)),
    ])
  },
  component: ReportDetailPage,
})

/** The source families an (Andre-model) topic is enabled for. */

function ReportDetailPage() {
  const { accountId, reportId } = Route.useParams()
  const { tab } = Route.useSearch()
  const { data: report } = useSuspenseQuery(reportQuery(reportId))
  const { data: listsData } = useSuspenseQuery(coverageListsQuery(accountId))
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const queryClient = useQueryClient()
  const updateReport = useUpdateReport(reportId, queryClient)
  // Deleting writes to the sub-account, which an account:edit grant cascades into — the same
  // permission the create button reads. The API gate is the boundary; this only hides the control.
  const canEdit = useCanEditAccount(accountId)

  // Auto-save: the draft is authoritative while typing; every patch schedules a debounced
  // PATCH of the whole settings document. Structural edits elsewhere save immediately.
  const [draft, setDraft] = useState<ReportSettings>(report.settings)
  const draftRef = useRef(draft)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const patch = (next: Partial<ReportSettings>) => {
    const merged = { ...draftRef.current, ...next }
    draftRef.current = merged
    setDraft(merged)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => updateReport.mutate({ settings: draftRef.current }), 600)
  }
  // Clear the pending timer on unmount so a navigation away can't fire a stale save.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const saveCoverage = (field: string, values: string[], refIds: string[]) =>
    updateReport.mutate({ coverage: withField(report.coverage, field, values, refIds) })

  const lists = listsData.lists

  return (
    <div className="flex flex-col gap-5">
      {/* The bin stays in the top-right corner whatever the width: the action buttons wrap under
          the title on a narrow screen, the one destructive control does not move with them. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-1 flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <PageHeader title={report.name} description="Report configuration" className="mb-0" />
            {/* Active / Inactive under the name — the state at a glance, not a Pause action. */}
            <ReportStatusToggle
              status={report.status}
              disabled={updateReport.isPending}
              onChange={(status) => updateReport.mutate({ status })}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground" aria-live="polite">
              {updateReport.isPending ? 'Saving…' : 'All changes saved'}
            </span>
            <ExportReportConfigButton report={report} />
            <PreviewTodayButton report={report} />
            {/* Configuration says what the report is; previous reports are what went out. Next door. */}
            <Link
              to="/app/accounts/$accountId/reports/$reportId/issues"
              params={{ accountId, reportId }}
              className={buttonVariants({ size: 'sm' })}
            >
              Previous reports
            </Link>
          </div>
        </div>
        {canEdit && (
          <DeleteReportButton
            accountId={accountId}
            reportId={reportId}
            reportName={report.name}
            subaccountId={report.subaccountId}
          />
        )}
      </div>

      <ReportTabsNav tab={tab} />

      {updateReport.isError && (
        <p role="alert" className="text-sm text-destructive">
          {updateReport.error.message}
        </p>
      )}

      {tab === 'delivery' && (
        <DeliveryTab
          draft={draft}
          patch={patch}
          reportId={report.id}
          accountId={accountId}
          owners={report.owners}
        />
      )}
      {tab === 'content' && (
        <ContentTab
          accountId={accountId}
          draft={draft}
          patch={patch}
          coverage={report.coverage}
          lists={lists}
          onCoverage={saveCoverage}
        />
      )}
      {tab === 'headers' && <HeadersTab accountId={accountId} report={report} lists={lists} />}
      {tab === 'derek' && <DerekTab report={report} draft={draft} patch={patch} />}
      {tab === 'template' && (
        <TemplateTab
          accountId={accountId}
          accountName={account.name}
          accountBrand={account.brand}
          report={report}
          onSelect={(templateId) => updateReport.mutate({ templateId })}
          // Through the draft, like every other settings edit: a direct save of `report.settings`
          // here was reverted by the next debounced draft PATCH, which still held the old brand.
          onBrandChange={(brand) => patch({ brand })}
          brand={draft.brand}
          prose={draft.prose}
          onProseChange={(prose) => patch({ prose })}
          saving={updateReport.isPending}
        />
      )}
    </div>
  )
}

/** The tab strip — each tab is a link to the same page with a different `tab` search param. */
function ReportTabsNav({ tab }: { tab: Tab }) {
  return (
    <nav aria-label="Report configuration tabs" className="flex gap-1 border-b border-border">
      {TABS.map((key) => (
        <Link
          key={key}
          from={Route.fullPath}
          search={{ tab: key }}
          aria-current={tab === key ? 'page' : undefined}
          className={
            tab === key
              ? '-mb-px border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground'
              : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
          }
        >
          {TAB_LABEL[key]}
        </Link>
      ))}
    </nav>
  )
}
